import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Upload, Loader2, AlertTriangle, CheckCircle2, FileText } from "lucide-react";

export default function BulkResumeImport({ open, onClose, onDone }) {
  const [rows, setRows] = useState(null);
  const [batchId, setBatchId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const preview = async (files) => {
    if (!files?.length) return;
    if (files.length > 100) { toast.error("Upload at most 100 resumes per batch"); return; }
    setBusy(true); setResult(null);
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/candidates/bulk-resumes/preview", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      setRows(data.rows); setBatchId(data.batch_id);
      toast.success(`${data.total} resumes read — ${data.importable} ready, ${data.duplicates} duplicate(s). Nothing saved yet.`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const setField = (i, k, v) => setRows((r) => r.map((row, j) => (j === i ? { ...row, parsed: { ...row.parsed, [k]: v } } : row)));

  const commit = async () => {
    const importable = rows.filter((r) => r.importable && r.parsed.full_name && !r.duplicates.length);
    if (!importable.length) { toast.error("No importable rows — resolve duplicates or add missing names"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/candidates/bulk-resumes/commit", {
        batch_id: batchId,
        rows: importable.map((r) => ({
          document_id: r.document_id, full_name: r.parsed.full_name,
          primary_email: r.parsed.primary_email || null, phones: r.parsed.phones || [],
          current_title: r.parsed.current_title || null, current_employer: r.parsed.current_employer || null,
          location: r.parsed.location || null, skills: r.parsed.skills || [],
          years_experience: r.parsed.years_experience ?? null, education: r.parsed.education || [],
          certifications: r.parsed.certifications || [], employment: r.parsed.employment || [] })),
      });
      setResult(data);
      toast.success(`${data.created_count} candidate(s) imported, ${data.skipped_count} skipped`);
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setRows(null); setResult(null); onClose?.(); } }}>
      <DialogContent className="max-h-[90vh] w-[95vw] max-w-4xl overflow-y-auto scrollbar-thin" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Bulk resume import</DialogTitle>
          <DialogDescription>Upload up to 100 PDF/DOC/DOCX resumes. Everything is previewed first — nothing is created until you confirm. Duplicates are skipped for you to resolve.</DialogDescription>
        </DialogHeader>

        {!rows && (
          <label className="flex cursor-pointer flex-col items-center gap-3 rounded-xl border-2 border-dashed border-border p-10 text-center transition-colors hover:border-[#255058]/50">
            <input type="file" multiple hidden accept=".pdf,.doc,.docx,.txt" data-testid="bulk-import-input" onChange={(e) => preview(e.target.files)} />
            {busy ? <Loader2 className="h-6 w-6 animate-spin text-[#255058]" /> : <Upload className="h-6 w-6 text-[#255058]" />}
            <span className="text-sm font-medium">{busy ? "Reading resumes…" : "Choose resumes"}</span>
            <span className="text-xs text-muted-foreground">Basic info is auto-extracted. Unknown values stay blank — nothing is invented.</span>
          </label>
        )}

        {rows && !result && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-800">{rows.filter((r) => r.importable).length} ready</Badge>
              <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">{rows.filter((r) => r.duplicates.length).length} duplicate</Badge>
              <Badge variant="outline">{rows.filter((r) => r.error).length} need attention</Badge>
            </div>
            {rows.map((r, i) => (
              <Card key={i} className="p-3" data-testid={`bulk-row-${i}`}>
                <div className="flex items-center gap-2 text-sm font-medium"><FileText className="h-4 w-4 text-[#255058]" />{r.filename}
                  {r.importable ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}
                </div>
                <div className="mt-2 grid gap-2 sm:grid-cols-4">
                  <Input placeholder="Full name" data-testid={`bulk-name-${i}`} value={r.parsed.full_name || ""} onChange={(e) => setField(i, "full_name", e.target.value)} />
                  <Input placeholder="Email" value={r.parsed.primary_email || ""} onChange={(e) => setField(i, "primary_email", e.target.value)} />
                  <Input placeholder="Phone" value={(r.parsed.phones || [])[0] || ""} onChange={(e) => setField(i, "phones", e.target.value ? [e.target.value] : [])} />
                  <Input placeholder="Current title" value={r.parsed.current_title || ""} onChange={(e) => setField(i, "current_title", e.target.value)} />
                </div>
                {!!r.duplicates.length && (
                  <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid={`bulk-dup-${i}`}>
                    {r.duplicates.map((d, k) => (
                      <div key={k}>Candidate already exists{d.owner_name ? ` — owner: ${d.owner_name}` : ""}{d.full_name ? ` (${d.full_name})` : ""} · matched on {(d.match_on || []).join(", ")}</div>
                    ))}
                    <div className="mt-1">Skipped — resolve this candidate manually before importing.</div>
                  </div>
                )}
                {r.error && <div className="mt-2 text-xs text-muted-foreground">{r.error}</div>}
              </Card>
            ))}
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setRows(null)}>Start over</Button>
              <Button className="flex-1 bg-[#255058] text-white hover:bg-[#1c3d43]" disabled={busy} onClick={commit} data-testid="bulk-import-commit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Import ${rows.filter((r) => r.importable && r.parsed.full_name && !r.duplicates.length).length} candidate(s)`}
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3" data-testid="bulk-import-result">
            <div className="text-sm font-medium">{result.created_count} candidate(s) created · {result.skipped_count} skipped</div>
            {result.skipped.map((s, i) => <div key={i} className="text-xs text-muted-foreground">{s.full_name}: {s.reason}</div>)}
            <Button className="w-full bg-[#255058] text-white hover:bg-[#1c3d43]" onClick={() => { setRows(null); setResult(null); onClose?.(); }}>Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
